"use client"

import { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { OrganizationContext } from "@/contexts/organization"
import { useMainApiSocket } from "@/contexts/websocket"
import { CircleDollarSign } from "lucide-react"
import { FormattedMessage } from "react-intl"

import { cn } from "@/lib/utils"
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card"

type CreditsProps = {
  classes?: {
    container?: string
    icon?: string
    text?: string
  }
}

export function Credits({ classes }: CreditsProps) {
  const { selectedOrganization, organizationCredits, setOrganizationCredits } =
    useContext(OrganizationContext)
  const [animation, setAnimation] = useState("")
  const socket = useMainApiSocket()
  const getOrgCredits = useCallback(async () => {
    const res = await fetch(`/api/organizations/credits`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })
    const data = await res.json()
    if (data.message === "CREDS_RETRIEVED_SUCCESSFULLY" && data.data) {
      setOrganizationCredits({
        current_credits: data.data.current_credits,
        total_credits: data.data.total_credits,
      })
    }
  }, [selectedOrganization])
  const displayCredits = useMemo(
    () =>
      organizationCredits ? `${organizationCredits.current_credits}` : "0",
    [organizationCredits?.current_credits]
  )

  useEffect(() => {
    getOrgCredits()
  }, [getOrgCredits])

  useEffect(() => {
    if (socket == null) return

    socket.on("ORG_CREDITS", (message) => {
      setAnimation("slide-down")
      setTimeout(() => {
        setOrganizationCredits(message)
        setAnimation("") // Reset animation
      }, 200)
      setTimeout(() => {
        setAnimation("slide-up") // Start the slide-up animation after a delay
      }, 400)
    })

    return () => {
      socket.off("ORG_CREDITS")
    }
  }, [socket])

  return (
    <>
      <HoverCard>
        <HoverCardTrigger>
          <div
            className={cn(
              "justify-left w-30 flex items-center pe-6 text-xs",
              classes?.container
            )}
          >
            <CircleDollarSign className={cn("mr-2 h-4 w-4", classes?.icon)} />
            <span
              className={cn(
                "text-primary transition-transform duration-200 ease-out",
                animation === "slide-down"
                  ? "translate-y-[-100%] opacity-0"
                  : "translate-y-0 opacity-100",
                classes?.text
              )}
            >
              {displayCredits}
            </span>
            /<span>{organizationCredits?.total_credits}</span>
          </div>
        </HoverCardTrigger>
        <HoverCardContent align="end">
          <FormattedMessage id="credits" />
        </HoverCardContent>
      </HoverCard>
    </>
  )
}
